import React, { useEffect, useRef, useCallback } from 'react';
import { Outlet, useLocation } from '@tanstack/react-router';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import WebDesignedResizableNavbar from '../webdesigned/WebDesignedResizableNavbar';
import WebDesignedFloatingDock from '../webdesigned/WebDesignedFloatingDock';
import WebDesignedCartAssistant from '../webdesigned/WebDesignedCartAssistant';
import MobileFloatingDock from '../webdesigned/05B-mobile/MobileFloatingDock';
import PadFloatingDock from '../webdesigned/05C-pad/PadFloatingDock';
import { resolveExperienceMode, type ExperienceMode } from '../webdesigned/core/resolveExperienceMode';
import { WebDesignedCartProvider, useWebDesignedCart } from '../../contexts/WebDesignedCartContext';
import { useAuth } from '../../contexts/AuthContext';
import { clearCart, removeVumIncludedActivities } from '../../services/cartService';
import type { User as AppUser } from '../../types';
import { sanitizePlainText } from '../../security/frontend';
import WebDesignedPortalSlideOuts from './WebDesignedPortalSlideOuts';
import { VumPromoModal } from '../webdesigned/VumPromoModal';
import { CartDateLockManager } from '../webdesigned/CartDateLockManager';
import { WEBDESIGNED_HEADER_HEIGHT_PX } from './webDesignedPortalUtils';

function WebDesignedPortalContent({ children }: { children?: React.ReactNode }) {
  const cart = useWebDesignedCart()!;
  const { user, setUser, isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const prevUserRef = useRef<unknown>(undefined);
  const [headerOffsetPx, setHeaderOffsetPx] = React.useState(WEBDESIGNED_HEADER_HEIGHT_PX);
  const [experienceMode, setExperienceMode] = React.useState<ExperienceMode>(() =>
    typeof window === 'undefined' ? 'desktop' : resolveExperienceMode(window.innerWidth)
  );

  const portalShellRef = useRef<HTMLDivElement>(null);

  const handleHeaderOffsetChange = useCallback((px: number) => {
    setHeaderOffsetPx(px);
    const shell = portalShellRef.current;
    if (!shell) return;
    shell.style.setProperty('--wd-header-offset', `${px}px`);
    // Content padding uses top-of-page height so scroll chrome doesn't reflow the page.
    if (window.scrollY < 24) {
      shell.style.setProperty('--wd-header-clearance', `${px}px`);
    }
  }, []);

  useEffect(() => {
    const onResize = () => {
      setExperienceMode(resolveExperienceMode(window.innerWidth));
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const stateAuth = (location.state as { openAuth?: string } | undefined)?.openAuth;
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const queryAuth = params.get('auth');
    const auth = stateAuth || queryAuth;
    if (auth === 'signin' || auth === 'signup') {
      cart.setAuthPanelMode(auth === 'signup' ? 'signup' : 'signin');
      cart.setAuthPanelOpen(true);
      if (queryAuth && typeof window !== 'undefined') {
        window.history.replaceState(window.history.state ?? {}, '', window.location.pathname);
      }
    }
  }, [location.state, location.search, cart]);

  useEffect(() => {
    const handleUserLoggedIn = (event: CustomEvent) => {
      const u = event.detail;
      if (u?.firstName && setUser) {
        const appUser: AppUser = {
          id: u.id,
          email: u.email,
          firstName: u.firstName,
          lastName: u.lastName,
          phone: u.phone ?? '',
          role: (u.role as AppUser['role']) ?? 'client',
          preferences: { language: 'fr', notifications: true },
        };
        setUser(appUser);
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('currentUser', JSON.stringify(appUser));
          sessionStorage.setItem('isManager', String(u.role === 'manager'));
        }
      }
    };
    window.addEventListener('userLoggedIn', handleUserLoggedIn as EventListener);
    return () => window.removeEventListener('userLoggedIn', handleUserLoggedIn as EventListener);
  }, [setUser]);

  useEffect(() => {
    if (prevUserRef.current !== undefined && prevUserRef.current !== null && !user) {
      clearCart();
    }
    prevUserRef.current = user;
  }, [user]);

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      clearCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  const upsertGuest = useMutation(api.guests.upsert);
  const createBooking = useMutation(api.bookings.create);
  const saveUserAddress = useMutation(api.users.saveSignUpAddress);
  const markVumBooked = useMutation(api.vumCodes.markBooked);

  const handleReservationFormSubmit = useCallback(
    async (reservationData: unknown) => {
      if (typeof reservationData !== 'object' || reservationData === null) return;
      const d = reservationData as {
        itemType?: string;
        itemId?: string;
        firstName?: string;
        lastName?: string;
        email?: string;
        phone?: string;
        checkIn?: string;
        checkOut?: string;
        guests?: number;
        adults?: number;
        children?: number;
        itemPrice?: number;
        depositAmount?: number;
        balanceAmount?: number;
        gstAmount?: number;
        qstAmount?: number;
        taxeHebergementAmount?: number;
        paymentMethod?: string;
        specialRequests?: string;
        hearAboutUs?: string;
        isGuest?: boolean;
        address?: {
          streetNumber?: string;
          street?: string;
          aptNumber?: string;
          city?: string;
          province?: string;
          postalCode?: string;
          country?: string;
        };
      };
      if (
        d.itemType === 'accommodation' &&
        d.itemId &&
        d.email &&
        d.checkIn &&
        d.checkOut != null &&
        typeof d.itemPrice === 'number'
      ) {
        try {
          const addr = d.address;
          const addressLine1 =
            addr &&
            [addr.streetNumber, addr.street, addr.aptNumber]
              .map((s) => (s == null ? '' : String(s).trim()))
              .filter(Boolean)
              .join(' ')
              .trim();
          const hasAddress = Boolean(addr && addressLine1 && (addr.postalCode || '').trim());
          const guestId = await upsertGuest({
            firstName: d.firstName || 'Guest',
            lastName: d.lastName || '',
            email: d.email,
            phone: d.phone || undefined,
            address: hasAddress
              ? {
                  addressLine1,
                  province: addr!.province || 'QC',
                  postalCode: addr!.postalCode || '',
                  country: addr!.country || 'CA',
                }
              : undefined,
          });

          if (!d.isGuest && hasAddress && d.email) {
            try {
              await saveUserAddress({
                email: d.email,
                addressLine1,
                province: addr!.province || 'QC',
                postalCode: addr!.postalCode || '',
                country: addr!.country || 'CA',
              });
            } catch (err) {
              console.warn('Failed to save user address', err);
            }
          }

          const adults = d.adults ?? 1;
          const children = d.children ?? 0;
          const isPartial = d.paymentMethod === 'partial';
          const isPaid =
            d.paymentMethod === 'credit' ||
            d.paymentMethod === 'debit' ||
            d.paymentMethod === 'full' ||
            isPartial;
          const bookingId = await createBooking({
            accommodationId: d.itemId as Id<'accommodations'>,
            guestId,
            checkIn: d.checkIn,
            checkOut: d.checkOut,
            guests: adults + children,
            adults,
            children,
            totalPrice: d.itemPrice!,
            paid: isPaid,
            status: isPaid ? 'confirmed' : 'pending',
            paymentMethod: d.paymentMethod || 'credit',
            depositAmount: d.depositAmount,
            balanceAmount: d.balanceAmount,
            gstAmount: d.gstAmount,
            qstAmount: d.qstAmount,
            taxeHebergementAmount: d.taxeHebergementAmount,
            specialRequests: d.specialRequests ? sanitizePlainText(d.specialRequests, 300) : undefined,
            hearAboutUs: d.hearAboutUs || undefined,
            isGuest: d.isGuest ?? true,
            temporaryReservationId: (d as any).temporaryReservationId || undefined,
            temporaryReservationSessionId: (d as any).temporaryReservationSessionId || undefined,
            promoVum: cart.vumPromo ? true : undefined,
          });

          const vumCodeId = cart.vumPromo?.vumCodeId;
          if (vumCodeId) {
            try {
              await markVumBooked({
                id: vumCodeId as Id<'vumCodes'>,
                bookingId,
              });
              cart.setVumPromo(null);
              removeVumIncludedActivities();
            } catch (err) {
              console.warn('Failed to mark VUM code as booked', err);
            }
          }
        } catch (err) {
          console.error('Failed to create booking', err);
        }
      }
    },
    [upsertGuest, createBooking, saveUserAddress, markVumBooked, cart]
  );

  const handleCheckoutReservationSubmit = (reservationData: unknown) => {
    try {
      sessionStorage.setItem('pendingReservation', JSON.stringify({ reservationData }));
    } catch (e) {
      console.error('Failed to save reservation to session storage', e);
    }
  };

  const handleMainContentClick = () => {
    if (cart.cartSidebarOpen) cart.setCartSidebarOpen(false);
    if (cart.checkout.isOpen) cart.closeCheckout();
    if (cart.authPanelOpen) cart.setAuthPanelOpen(false);
    if (cart.reservationForm.isOpen) cart.closeReservationForm();
  };

  return (
    <div ref={portalShellRef} className="webdesigned-ui relative min-h-screen">
      <WebDesignedResizableNavbar onOffsetChange={handleHeaderOffsetChange} />

      {experienceMode === 'mobile' ? (
        <MobileFloatingDock />
      ) : experienceMode === 'pad' ? (
        <PadFloatingDock />
      ) : (
        <WebDesignedFloatingDock />
      )}
      <WebDesignedCartAssistant topOffset={headerOffsetPx} />

      <div
        className="min-h-screen transition-all duration-300"
        style={{ backgroundColor: 'transparent' }}
        onClick={handleMainContentClick}
      >
        {children ?? <Outlet />}
      </div>

      <WebDesignedPortalSlideOuts
        cart={cart}
        headerOffsetPx={headerOffsetPx}
        user={user}
        onReservationFormSubmit={handleReservationFormSubmit}
        onCheckoutReservationSubmit={handleCheckoutReservationSubmit}
      />
      <VumPromoModal />
      <CartDateLockManager />
    </div>
  );
}

/**
 * Webdesigned portal layout: Header + content + Shopping Assistant + Cart sidebar.
 * Use as layout (Outlet) or wrap page: <WebDesignedPortal><YourPage /></WebDesignedPortal>.
 */
const WebDesignedPortal: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <WebDesignedCartProvider>
    <WebDesignedPortalContent>{children}</WebDesignedPortalContent>
  </WebDesignedCartProvider>
);

export default WebDesignedPortal;
